import axios, { AxiosResponse } from 'axios'
import IdTokenVerifier from 'idtoken-verifier'
import { createOriginFromURL, getWellKnownUrl } from './urls'
import { injectBearer } from './bearer'
import isDevEnv from './isDevEnv'

import { Algorithm, JwtPayload, VerifyOptions } from 'jsonwebtoken'

/*
    _______________________________________________
   | TOKENS                                        |
   |_______________________________________________|
   | Helper methods and logic for token validation |
   |_______________________________________________|
*/

/**
 * AccessTokenTypes used to limit options: either 'Bearer' or an empty string
 * @typedef {"Bearer" | ""} AccessTokenType
 */
export type AccessTokenType = 'Bearer' | ''

interface WellKnownResponse {
  issuer: string
  authorization_endpoint: string
  token_endpoint: string
  jwks_uri: string
  response_types_supported: string[]
  subject_types_supported: string[]
  id_token_signing_alg_values_supported: Algorithm[]
  userinfo_endpoint: string
  registration_endpoint: string
  scopes_supported: string[]
  claims_supported: string[]
  grant_types_supported: string[]
  acr_values_supported: string[]
  token_endpoint_auth_methods_supported: string[]
  display_values_supported: string[]
  claim_types_supported: string[]
  service_documentation: string
  ui_locales_supported: string[]
  userinfo_signing_alg_values_supported: Algorithm[]
}
export interface UserInfoResponse {
  sub: string
  firstname: string
  lastname: string
  mail: string
  lanid: string
  memberof: string[]
}
// fetches user info from an API
// this does not give all the information that would normally be found in an ID token
// used when only access token is provided and no ID token (Cypress tests)
export const fetchUserInfo = async (
  accessToken: string,
  authorizationUrl: string,
): Promise<UserInfoResponse | null> => {
  // Convert authorizationURL from props to well-known URL
  const wellKnownURL = getWellKnownUrl(authorizationUrl)
  if (!wellKnownURL) return null
  try {
    const response: AxiosResponse<WellKnownResponse> = await axios.get(
      wellKnownURL.toString(),
    )
    const wellKnown = response.data
    const userInfoEndpoint = wellKnown['userinfo_endpoint']
    const userInfo: AxiosResponse<UserInfoResponse> = await axios.get(
      userInfoEndpoint,
      {
        headers: { Authorization: injectBearer(accessToken) },
      },
    )
    return userInfo.data
  } catch {
    return null
  }
}

/**
 * JSON Web Key definition
 * @see https://datatracker.ietf.org/doc/html/rfc7517
 */
export interface JWK {
  kty: string
  kid: string
  use: string
  n: string
  e: string
  x5c: string[]
}
export interface TargetVerifyOptions extends VerifyOptions {
  jwksUri: string
}

export const stringifyOrDefault = (
  param: string | RegExp | Array<string | RegExp> | undefined,
) => {
  return param?.toString() || ''
}

export const verifyIdentityToken = async (
  token: string,
  options: TargetVerifyOptions,
): Promise<any> => {
  return new Promise((resolve, reject) => {
    const verifier = createTokenDecoder({
      issuer: stringifyOrDefault(options.issuer),
      audience: stringifyOrDefault(options.audience),
      jwksURI: options.jwksUri,
      jwksCache: new Map(),
    })
    verifier.verify(
      token,
      stringifyOrDefault(options.nonce),
      (err, payload) => {
        if (err) reject(err)
        resolve(payload)
      },
    )
  })
}

export const verifyAccessToken = async (
  token: string,
  options: TargetVerifyOptions,
  alg: string,
  atHash: string,
): Promise<any> => {
  return new Promise<void>((resolve, reject) => {
    const verifier = createTokenDecoder({
      issuer: stringifyOrDefault(options.issuer),
      audience: stringifyOrDefault(options.audience),
      jwksURI: options.jwksUri,
      jwksCache: new Map(),
    })
    verifier.validateAccessToken(token, alg, atHash, (err) => {
      if (err) reject(err)
      resolve()
    })
  })
}

export interface TargetJwtPayload extends JwtPayload {
  /**
   * @note GSP claim
   * @see kid
   */
  sky?: string
  /**
   * subject type
   * @note GSP claim
   * @example "G" // indicates anonymous guests
   * @example "R" // indicates logged in guest
   */
  sut?: string
}

/** The body of a decoded access token */
export interface Access extends TargetJwtPayload {
  /** subject */
  sub?: string
  /** user lanId */
  username: string
  /** scopes client has access to */
  scope: string[]
  /** OAuth client ID used to create tokens */
  client_id: string
  /** direct use of a shared symmetric key */
  dir: string
  /** value used to associate a client session with JSON Web Tokens */
  nonce?: string
  /**
   * email ID for logged in guests
   * @note GSP claim
   */
  eid?: string
  /**
   * scopes client has access to
   * @note GSP claim
   * @note "." separated values
   * @see scope
   */
  sco?: string
}

export interface IdentityBase extends TargetJwtPayload {
  /** subject */
  sub: string
  /**
   * assurance
   * @note GSP claim
   * @example "L"
   */
  ass?: string
  /** time when the authentication occurred */
  auth_time: number
  /** value used to associate a client session with JSON Web Tokens */
  nonce: string
  /** client ID */
  cli?: string
  /**
   * profile
   * @note GSP claim
   */
  pro?: {
    fn: null
    em: null
    ph: boolean
    led: null
    lty: boolean
  }
  /** authorized party - party to which the ID token was issued */
  azp: string
  /** audience */
  aud: string
  /** access token hash value */
  at_hash: string
  /** authentication context class reference */
  acr: string
  /** client ID*/
  client_id?: string
  /** user email */
  mail: string
  /** user first name */
  firstname: string
  /** user last name */
  lastname: string
  /** user lan ID */
  samaccountname: string
  /** user AD group affiliation */
  memberof: string[]
}

/** The body of a decoded identity token */
export interface Identity extends IdentityBase {
  [key: string]: any // advanced client config
}

/**
 * Validates provided tokens, with an emphasis on id tokens.
 *
 * Our front-end emphasis is on id tokens as the primary interface that we and our users care about.
 * Access tokens are meant to be passed to backend APIs. Front ends have little to no reason to inspect their contents, aside from peeking at `expires` within Session.
 *
 * [1] gathers necessary materials needed to validate the tokens from a well-known endpoint
 * [2] validate identity token first, if provided. We validate idToken first because the front end doesn't really do much with an accessToken, and you can use info in the idToken to validate the accessToken.
 * [3] retrieve info from the header from the id token
 * [4] use the information from the id token to validate the access token
 * @param accessToken      - access token to be validated
 * @param idToken          - ID token to be validated
 * @param authorizationUrl - the authorization URL used to generate the tokens, used to get keys and other validation materials
 * @param clientId         - the client ID used to generate the tokens, used to validate the ID token
 * @param nonce            - unique value that was used to generate tokens, used to validate ID token and protect against replay attacks
 * @returns a promise of whether both tokens are valid or not
 */
export const tokensAreValid = async (
  accessToken: string,
  idToken: string,
  authorizationUrl: string,
  clientId: string,
  nonce: string,
): Promise<boolean> => {
  if (!idToken || !accessToken) {
    return false // no accessToken or idToken provided
  }

  // [1] gathers necessary materials needed to validate the tokens from a well-known endpoint
  // Convert authorizationURL from props to well-known URL
  const wellKnownURL = getWellKnownUrl(authorizationUrl)
  if (!wellKnownURL) {
    console.error('Could not get well-known url')
    return false // couldn't generate a well-known url, bad authorization URL
  }

  let jwksUri: string
  try {
    const wellKnown: AxiosResponse<WellKnownResponse> = await axios.get(
      wellKnownURL.toString(),
    )
    jwksUri = wellKnown.data.jwks_uri
  } catch {
    return false // couldn't get well-known URL response
  }

  const idConfig = {
    audience: clientId, // make sure ID token was generated for this client ID
    issuer: createOriginFromURL(authorizationUrl), // make sure ID token was generated from this host
    nonce, // make sure ID token was generated from the same flow
    jwksUri, // where to retrieve the JWKS from (see https://auth0.com/docs/security/tokens/json-web-tokens/json-web-key-sets)
  }
  try {
    // [2] validate identity token and retrieve info from the payload
    const { at_hash } = await verifyIdentityToken(idToken, idConfig)
    if (at_hash) {
      // [3] retrieve info from the header from the id token
      const { alg } = decodeTokenProperty(idToken, 'header')
      if (alg) {
        // [4] use the information from the id token to validate the access token
        await verifyAccessToken(accessToken, idConfig, alg, at_hash)
      } else {
        return false // id token missing alg header
      }
    } else {
      return false // id token could not be verified, missing at_hash from header
    }
  } catch (e) {
    if (isDevEnv()) {
      console.error(e)
    }
    return false // tokens couldn't be verified
  }

  return true // all is well
}

export interface IDTokenVerifierParams {
  issuer: string
  audience: string
  jwksCache?: any
  jwksURI?: string
  expectedAlg?: string
  leeway?: number
  maxAge?: number
}

export const createTokenDecoder = (
  { issuer, audience, ...other }: IDTokenVerifierParams = {
    issuer: '', // not needed for `decode`
    audience: '', // not needed for `decode`
  },
) => {
  return new IdTokenVerifier({ issuer, audience, ...other })
}

/**
 * Decodes a well formed JWT without any verification and returns the property
 *
 * @param {string} token the token to decode
 * @param {string} property "header" or "payload"
 * @return {object} content of the JWT Header, or token claims from the payload
 */
export const decodeTokenProperty = (
  token: string,
  property: 'header' | 'payload',
) => {
  const decoder = createTokenDecoder()
  return decoder.decode(token)[property]
}
